import { Container } from '@/components/ui/Container'
import { Button } from '@/components/ui/Button'
import { t } from '@/utils/i18n'

interface HeroSlide {
  backgroundImage: string
  badgeKey: string
  headlineKey: string
  subheadlineKey: string
  primaryCta: { textKey: string; href: string }
  secondaryCta: { textKey: string; href: string }
  stats?: { numberKey: string; labelKey: string }[]
}

const slideConfigs: HeroSlide[] = [
  {
    backgroundImage: '/images/GIGAette_cover.webp',
    badgeKey: 'heroSlides.slide1.badge',
    headlineKey: 'heroSlides.slide1.headline',
    subheadlineKey: 'heroSlides.slide1.subheadline',
    primaryCta: { textKey: 'heroSlides.slide1.primaryCta', href: '/technology' },
    secondaryCta: { textKey: 'heroSlides.slide1.secondaryCta', href: '/projects' },
    stats: [
      { numberKey: 'heroSlides.slide1.stat1Number', labelKey: 'heroSlides.slide1.stat1Label' },
      { numberKey: 'heroSlides.slide1.stat2Number', labelKey: 'heroSlides.slide1.stat2Label' },
      { numberKey: 'heroSlides.slide1.stat3Number', labelKey: 'heroSlides.slide1.stat3Label' },
    ],
  },
  {
    backgroundImage: '/images/GIGAette_cover.webp',
    badgeKey: 'heroSlides.slide2.badge',
    headlineKey: 'heroSlides.slide2.headline',
    subheadlineKey: 'heroSlides.slide2.subheadline',
    primaryCta: { textKey: 'heroSlides.slide2.primaryCta', href: '/technology/how-it-works' },
    secondaryCta: { textKey: 'heroSlides.slide2.secondaryCta', href: '/technology/hts' },
    stats: [
      { numberKey: 'heroSlides.slide2.stat1Number', labelKey: 'heroSlides.slide2.stat1Label' },
      { numberKey: 'heroSlides.slide2.stat2Number', labelKey: 'heroSlides.slide2.stat2Label' },
      { numberKey: 'heroSlides.slide2.stat3Number', labelKey: 'heroSlides.slide2.stat3Label' },
    ],
  },
  {
    backgroundImage: '/images/GIGAette_cover.webp',
    badgeKey: 'heroSlides.slide3.badge',
    headlineKey: 'heroSlides.slide3.headline',
    subheadlineKey: 'heroSlides.slide3.subheadline',
    primaryCta: { textKey: 'heroSlides.slide3.primaryCta', href: '/projects/koica-cts-seed-1' },
    secondaryCta: { textKey: 'heroSlides.slide3.secondaryCta', href: '/projects' },
    stats: [
      { numberKey: 'heroSlides.slide3.stat1Number', labelKey: 'heroSlides.slide3.stat1Label' },
      { numberKey: 'heroSlides.slide3.stat2Number', labelKey: 'heroSlides.slide3.stat2Label' },
      { numberKey: 'heroSlides.slide3.stat3Number', labelKey: 'heroSlides.slide3.stat3Label' },
    ],
  },
]

interface ResolvedSlide {
  backgroundImage: string
  badge: string
  headline: string
  subheadline: string
  primaryCta: { text: string; href: string }
  secondaryCta: { text: string; href: string }
  stats?: { number: string; label: string }[]
}

function getSlides(): ResolvedSlide[] {
  return slideConfigs.map(config => ({
    backgroundImage: config.backgroundImage,
    badge: t(config.badgeKey),
    headline: t(config.headlineKey),
    subheadline: t(config.subheadlineKey),
    primaryCta: { text: t(config.primaryCta.textKey), href: config.primaryCta.href },
    secondaryCta: { text: t(config.secondaryCta.textKey), href: config.secondaryCta.href },
    stats: config.stats?.map(stat => ({
      number: t(stat.numberKey),
      label: t(stat.labelKey),
    })),
  }))
}

let currentSlide = 0
let autoPlayInterval: number | null = null

export function HeroSection(): HTMLElement {
  const slides = getSlides()
  const section = document.createElement('section')
  section.className = 'relative min-h-screen flex items-center overflow-hidden -mt-20 pt-20'

  // Background slides container
  const bgContainer = document.createElement('div')
  bgContainer.className = 'absolute inset-0'
  bgContainer.id = 'hero-backgrounds'

  slides.forEach((slide, index) => {
    const bg = document.createElement('div')
    bg.className = `absolute inset-0 transition-opacity duration-1000 ${index === 0 ? 'opacity-100' : 'opacity-0'}`
    bg.dataset.bgIndex = String(index)
    bg.style.backgroundImage = `url(${slide.backgroundImage})`
    bg.style.backgroundSize = 'cover'
    bg.style.backgroundPosition = 'center'
    bg.style.backgroundRepeat = 'no-repeat'
    bgContainer.appendChild(bg)
  })

  section.appendChild(bgContainer)

  // Dark overlay
  const overlay = document.createElement('div')
  overlay.className = 'absolute inset-0 bg-black/75'
  section.appendChild(overlay)

  // Content slides container
  const contentContainer = document.createElement('div')
  contentContainer.className = 'relative z-10 w-full'
  contentContainer.id = 'hero-contents'

  slides.forEach((slide, index) => {
    const content = createSlideContent(slide, index)
    content.className = `transition-opacity duration-700 ${index === 0 ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'}`
    content.dataset.contentIndex = String(index)
    contentContainer.appendChild(content)
  })

  const container = Container({
    children: contentContainer,
    className: 'py-20',
  })

  section.appendChild(container)

  // Navigation arrows
  const prevBtn = document.createElement('button')
  prevBtn.className = 'absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 group'
  prevBtn.innerHTML = `
    <svg class="w-6 h-6 transform group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
    </svg>
  `
  prevBtn.setAttribute('aria-label', t('heroSlides.prevSlide'))
  prevBtn.addEventListener('click', () => goToSlide(currentSlide - 1))

  const nextBtn = document.createElement('button')
  nextBtn.className = 'absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 group'
  nextBtn.innerHTML = `
    <svg class="w-6 h-6 transform group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
    </svg>
  `
  nextBtn.setAttribute('aria-label', t('heroSlides.nextSlide'))
  nextBtn.addEventListener('click', () => goToSlide(currentSlide + 1))

  section.appendChild(prevBtn)
  section.appendChild(nextBtn)

  // Dots navigation - using explicit white color
  const dotsContainer = document.createElement('div')
  dotsContainer.className = 'absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3'
  dotsContainer.id = 'hero-dots'

  slides.forEach((_, index) => {
    const dot = document.createElement('button')
    if (index === 0) {
      dot.className = 'h-3 w-8 rounded-full bg-orange-500 transition-all duration-300'
    } else {
      dot.className = 'h-3 w-3 rounded-full transition-all duration-300'
      dot.style.backgroundColor = 'rgba(255, 255, 255, 0.7)'
    }
    dot.setAttribute('aria-label', t('heroSlides.goToSlide').replace('{n}', String(index + 1)))
    dot.addEventListener('click', () => goToSlide(index))
    dotsContainer.appendChild(dot)
  })

  section.appendChild(dotsContainer)

  // Start auto-play
  startAutoPlay()

  // Pause on hover
  section.addEventListener('mouseenter', stopAutoPlay)
  section.addEventListener('mouseleave', startAutoPlay)

  return section
}

function createSlideContent(slide: ResolvedSlide, _index: number): HTMLElement {
  const content = document.createElement('div')
  content.className = 'max-w-5xl mx-auto text-center'

  // Badge wrapper for centering
  const badgeWrapper = document.createElement('div')
  badgeWrapper.className = 'flex justify-center mb-8 animate-fade-in-up'
  badgeWrapper.style.animationDelay = '0.2s'

  const badge = document.createElement('div')
  badge.className = 'inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md'

  const badgeDot = document.createElement('span')
  badgeDot.className = 'w-2 h-2 rounded-full bg-orange-500 animate-pulse'

  const badgeText = document.createElement('span')
  badgeText.className = 'text-sm font-medium text-orange-200 tracking-wide'
  badgeText.textContent = slide.badge

  badge.appendChild(badgeDot)
  badge.appendChild(badgeText)
  badgeWrapper.appendChild(badge)
  content.appendChild(badgeWrapper)

  // Headline with gradient
  const headline = document.createElement('h1')
  headline.className = 'text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 pb-2 leading-snug tracking-tight text-center mx-auto animate-fade-in-up'
  headline.style.animationDelay = '0.4s'
  headline.style.background = 'linear-gradient(135deg, #FFFFFF 0%, #F8B7A5 50%, #EF5730 100%)'
  headline.style.webkitBackgroundClip = 'text'
  headline.style.webkitTextFillColor = 'transparent'
  headline.style.backgroundClip = 'text'
  headline.style.filter = 'drop-shadow(0 4px 20px rgba(0,0,0,0.5))'
  headline.innerHTML = slide.headline

  content.appendChild(headline)

  // Subheadline
  const subheadline = document.createElement('p')
  subheadline.textContent = slide.subheadline
  subheadline.className = 'text-xl sm:text-2xl text-slate-200 mb-12 max-w-3xl mx-auto leading-relaxed font-light animate-fade-in-up'
  subheadline.style.animationDelay = '0.6s'
  content.appendChild(subheadline)

  // CTA Buttons
  const ctaContainer = document.createElement('div')
  ctaContainer.className = 'flex flex-col sm:flex-row gap-6 justify-center items-center animate-fade-in-up'
  ctaContainer.style.animationDelay = '0.8s'

  const primaryCta = Button({
    children: slide.primaryCta.text,
    href: slide.primaryCta.href,
    variant: 'primary',
    size: 'lg',
    className: 'min-w-[200px] shadow-glow hover:shadow-glow-hover',
  })

  const secondaryCta = Button({
    children: slide.secondaryCta.text,
    href: slide.secondaryCta.href,
    variant: 'secondary',
    size: 'lg',
    className: 'min-w-[200px] bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30',
  })

  ctaContainer.appendChild(primaryCta)
  ctaContainer.appendChild(secondaryCta)
  content.appendChild(ctaContainer)

  // Stats
  if (slide.stats) {
    const statsContainer = document.createElement('div')
    statsContainer.className = 'mt-24 grid grid-cols-1 sm:grid-cols-3 gap-6'

    slide.stats.forEach((stat, statIndex) => {
      const statCard = document.createElement('div')
      statCard.className = 'glass p-4 sm:p-6 lg:p-8 rounded-2xl text-center transform transition-all duration-300 hover:-translate-y-1 bg-white/10 hover:bg-white/5 animate-fade-in-up'
      statCard.style.animationDelay = `${1 + statIndex * 0.1}s`

      const number = document.createElement('div')
      number.className = 'text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 tracking-tight'
      number.textContent = stat.number

      const label = document.createElement('div')
      label.className = 'text-xs sm:text-sm text-slate-400 uppercase tracking-wider font-medium'
      label.textContent = stat.label

      statCard.appendChild(number)
      statCard.appendChild(label)
      statsContainer.appendChild(statCard)
    })

    content.appendChild(statsContainer)
  }

  return content
}

function goToSlide(index: number): void {
  const totalSlides = slideConfigs.length
  const newIndex = (index + totalSlides) % totalSlides

  // Update background slides
  const bgElements = document.querySelectorAll('#hero-backgrounds > div')
  bgElements.forEach((bg, i) => {
    if (i === newIndex) {
      bg.classList.remove('opacity-0')
      bg.classList.add('opacity-100')
    } else {
      bg.classList.remove('opacity-100')
      bg.classList.add('opacity-0')
    }
  })

  // Update content slides
  const contentElements = document.querySelectorAll('#hero-contents > div')
  contentElements.forEach((content, i) => {
    if (i === newIndex) {
      content.classList.remove('opacity-0', 'absolute', 'inset-0', 'pointer-events-none')
      content.classList.add('opacity-100')
    } else {
      content.classList.remove('opacity-100')
      content.classList.add('opacity-0', 'absolute', 'inset-0', 'pointer-events-none')
    }
  })

  // Update dots
  const dots = document.querySelectorAll('#hero-dots button')
  dots.forEach((dot, i) => {
    const dotEl = dot as HTMLElement
    if (i === newIndex) {
      dotEl.className = 'h-3 w-8 rounded-full bg-orange-500 transition-all duration-300'
      dotEl.style.backgroundColor = ''
    } else {
      dotEl.className = 'h-3 w-3 rounded-full transition-all duration-300'
      dotEl.style.backgroundColor = 'rgba(255, 255, 255, 0.7)'
    }
  })

  currentSlide = newIndex
}

function startAutoPlay(): void {
  if (autoPlayInterval) return
  autoPlayInterval = window.setInterval(() => {
    goToSlide(currentSlide + 1)
  }, 6000) // 6 seconds per slide
}

function stopAutoPlay(): void {
  if (autoPlayInterval) {
    clearInterval(autoPlayInterval)
    autoPlayInterval = null
  }
}
